import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthLayout } from '../components/auth/AuthLayout';
import { Banner, Button, Checkbox, Field, Input, inlineLinkClass } from '../components/ui';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'magic' | 'password'>('magic');

  const { register, registerMagic } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Bitte akzeptiere die AGB.');
      return;
    }

    if (mode === 'password') {
      if (password !== confirmPassword) {
        setError('Passwörter stimmen nicht überein.');
        return;
      }
      if (password.length < 8) {
        setError('Passwort muss mindestens 8 Zeichen lang sein.');
        return;
      }
    }

    setIsLoading(true);

    try {
      if (mode === 'magic') {
        await registerMagic(email);
      } else {
        await register(email, password);
      }
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.message || 'Registrierung fehlgeschlagen. Bitte versuch es noch einmal.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Konto anlegen"
      description="Kostenlos starten, jederzeit upgraden."
      footer={
        <p>
          Bereits ein Konto?{' '}
          <Link to="/login" className={`${inlineLinkClass} font-semibold`}>
            Jetzt anmelden
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Banner tone="danger">{error}</Banner>}

        <Field label="E-Mail-Adresse" htmlFor="email">
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.de"
            required
            autoComplete="email"
            disabled={isLoading}
          />
        </Field>

        {mode === 'password' && (
          <>
            <Field label="Passwort" htmlFor="password">
              <Input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                required
                autoComplete="new-password"
                disabled={isLoading}
                minLength={8}
              />
            </Field>

            <Field label="Passwort bestätigen" htmlFor="confirmPassword">
              <Input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                required
                autoComplete="new-password"
                disabled={isLoading}
                minLength={8}
              />
            </Field>
          </>
        )}

        <Checkbox
          id="terms"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
          disabled={isLoading}
          label={
            <>
              Ich akzeptiere{' '}
              <a href="/agb" target="_blank" rel="noopener noreferrer" className={inlineLinkClass}>
                AGB
              </a>{' '}
              und{' '}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className={inlineLinkClass}>
                Datenschutzerklärung
              </a>
            </>
          }
        />

        <Button type="submit" fullWidth loading={isLoading}>
          Kostenlos registrieren
        </Button>

        <p className="text-center text-sm text-muted">
          {mode === 'magic' ? (
            <>
              Möchtest du lieber ein Passwort festlegen?{' '}
              <button
                type="button"
                onClick={() => setMode('password')}
                className="font-semibold text-brand hover:underline"
              >
                Mit Passwort registrieren
              </button>
            </>
          ) : (
            <>
              Passwort doch nicht nötig?{' '}
              <button
                type="button"
                onClick={() => setMode('magic')}
                className="font-semibold text-brand hover:underline"
              >
                Nur mit E-Mail registrieren
              </button>
            </>
          )}
        </p>
      </form>
    </AuthLayout>
  );
};

export default Register;
